import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, IdCard, Users, Clock, CheckCircle2 } from "lucide-react";
import SEO from "../../components/SEO";
import { REGISTRATION_LINKS } from "../../lib/constants";

export default function HowToBuyMedicalCannabisDC() {
  return (
    <>
      <SEO
        title="How to Buy Medical Cannabis in Washington, DC | DC & Out-of-State Patient Guide"
        description="A simple guide to purchasing medical cannabis in Washington, DC. Self-certify online in minutes as a DC resident, or register as an out-of-state visitor. Trusted guidance from District Wellness Center."
        path="/how-to-buy-medical-cannabis-dc"
        canonical="/blog/how-to-buy-medical-cannabis-dc"
        ogType="article"
        breadcrumbs={[
          { name: "Blog", path: "/blog" },
          { name: "How to Buy Medical Cannabis in DC", path: "/blog/how-to-buy-medical-cannabis-dc" },
        ]}
        article={{
          publishedTime: "2026-05-12",
          modifiedTime: "2026-09-15",
          section: "Patient Guide",
        }}
      />

      {/* HERO */}
      <section className="bg-white border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 py-24 sm:py-32">
          <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-5">
            DC & Out-of-State Patient Guide
          </div>
          <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight max-w-5xl">
            How to purchase <em className="text-brand">medical cannabis</em> in Washington, DC.
          </h1>
          <p className="mt-6 text-ink-muted text-base sm:text-lg max-w-2xl leading-relaxed">
            At District Wellness Center, we believe that accessing safe, regulated
            medical cannabis should be simple. Whether you&rsquo;re a Washington, DC
            resident or visiting from another state, there are easy ways to become
            eligible to purchase medical cannabis from our licensed dispensary. Here&rsquo;s
            everything you need to know.
          </p>
        </div>
      </section>

      {/* ARTICLE */}
      <article className="py-20 bg-[#FAFAFA]" data-testid="blog-how-to-buy">
        <div className="max-w-3xl mx-auto px-6 font-sans text-ink-muted">
          {/* DC RESIDENTS */}
          <div className="flex items-center gap-3 mb-4" data-testid="section-dc-residents">
            <div className="w-10 h-10 bg-brand/10 flex items-center justify-center">
              <IdCard size={20} className="text-brand" />
            </div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-brand">
              Section 01
            </div>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl text-ink mt-2 mb-5">
            For Washington, DC residents
          </h2>
          <p className="text-lg leading-relaxed">
            If you&rsquo;re 21 years of age or older and live in Washington, DC, you
            can purchase medical cannabis by completing the District&rsquo;s
            self-certification process.
          </p>
          <p className="text-lg leading-relaxed mt-4">
            The best part?{" "}
            <strong className="text-ink">
              You do not need a doctor&rsquo;s recommendation.
            </strong>
          </p>
          <p className="text-lg leading-relaxed mt-4">
            The online application only takes about 5–10 minutes to complete, and
            once approved, you&rsquo;ll receive your digital patient registration
            so you can begin shopping at licensed DC dispensaries like District
            Wellness Center.
          </p>

          <h3 className="font-serif text-xl text-ink mt-10 mb-4">
            What you&rsquo;ll need
          </h3>
          <ul className="space-y-3 mb-8">
            <li className="flex gap-3 text-base">
              <CheckCircle2 size={18} className="text-brand mt-0.5 shrink-0" />
              <span>A valid government-issued photo ID</span>
            </li>
            <li className="flex gap-3 text-base">
              <CheckCircle2 size={18} className="text-brand mt-0.5 shrink-0" />
              <span>A few minutes to complete the online application</span>
            </li>
            <li className="flex gap-3 text-base">
              <CheckCircle2 size={18} className="text-brand mt-0.5 shrink-0" />
              <span>An email address to receive your digital registration</span>
            </li>
          </ul>
          <p className="text-base italic leading-relaxed">
            Our team is also happy to help guide you through the process if you
            need assistance.
          </p>

          <a
            href={REGISTRATION_LINKS.medical}
            target="_blank"
            rel="noreferrer"
            className="mt-8 inline-flex items-center gap-2 bg-ink text-white hover:bg-brand px-6 py-4 text-[11px] uppercase tracking-[0.22em] transition-colors"
            data-testid="blog-register-medical-btn"
          >
            Register for Medical Cannabis <ArrowUpRight size={14} />
          </a>

          <div className="my-16 border-t border-black/10" />

          {/* OUT OF STATE */}
          <div className="flex items-center gap-3 mb-4" data-testid="section-out-of-state">
            <div className="w-10 h-10 bg-brand/10 flex items-center justify-center">
              <Users size={20} className="text-brand" />
            </div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-brand">
              Section 02
            </div>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl text-ink mt-2 mb-5">
            Visiting Washington, DC? Out-of-state patients are welcome.
          </h2>
          <p className="text-lg leading-relaxed">
            If you&rsquo;re visiting from another state, there are two ways you
            can legally purchase medical cannabis in Washington, DC.
          </p>

          {/* Option 1 */}
          <h3 className="font-serif text-2xl text-ink mt-10 mb-4">
            Option 1 · Use your existing medical cannabis card
          </h3>
          <p className="text-base leading-relaxed">
            If your home state participates in DC&rsquo;s reciprocity program,
            simply bring:
          </p>
          <ul className="space-y-3 my-4">
            <li className="flex gap-3 text-base">
              <CheckCircle2 size={18} className="text-brand mt-0.5 shrink-0" />
              <span>
                Your valid medical cannabis card (or qualifying registration where
                applicable)
              </span>
            </li>
            <li className="flex gap-3 text-base">
              <CheckCircle2 size={18} className="text-brand mt-0.5 shrink-0" />
              <span>A valid government-issued photo ID</span>
            </li>
          </ul>
          <p className="text-base leading-relaxed">
            You&rsquo;ll be able to shop at District Wellness Center without
            purchasing a temporary DC registration, provided your state qualifies
            under the District&rsquo;s reciprocity program.
          </p>

          {/* Option 2 */}
          <h3 className="font-serif text-2xl text-ink mt-10 mb-4">
            Option 2 · Register as a temporary DC patient
          </h3>
          <p className="text-base leading-relaxed">
            Don&rsquo;t have a medical cannabis card? No problem.
          </p>
          <p className="text-base leading-relaxed mt-4">
            Visitors who are 21 years of age or older can quickly register for a
            temporary DC medical cannabis patient registration online by
            completing a brief self-certification form.
          </p>
          <p className="text-base leading-relaxed mt-4">
            The process typically takes less than 10 minutes, and once payment is
            processed, your digital registration is emailed to you and can be
            used immediately.
          </p>

          <h4 className="font-serif text-xl text-ink mt-8 mb-3 flex items-center gap-2">
            <Clock size={18} className="text-brand" /> Temporary registration
            options
          </h4>
          <div className="bg-white border border-black/10 overflow-hidden mb-6">
            <table className="w-full text-base">
              <thead className="bg-ink text-white text-[10px] uppercase tracking-[0.25em]">
                <tr>
                  <th className="text-left px-5 py-3">Length</th>
                  <th className="text-right px-5 py-3">Cost</th>
                </tr>
              </thead>
              <tbody className="text-ink">
                <tr className="border-t border-black/5"><td className="px-5 py-3">3 Days</td><td className="text-right px-5 py-3 font-serif">$10</td></tr>
                <tr className="border-t border-black/5"><td className="px-5 py-3">30 Days</td><td className="text-right px-5 py-3 font-serif">$20</td></tr>
                <tr className="border-t border-black/5"><td className="px-5 py-3">90 Days</td><td className="text-right px-5 py-3 font-serif">$50</td></tr>
                <tr className="border-t border-black/5"><td className="px-5 py-3">180 Days</td><td className="text-right px-5 py-3 font-serif">$75</td></tr>
                <tr className="border-t border-black/5"><td className="px-5 py-3">365 Days</td><td className="text-right px-5 py-3 font-serif">$100</td></tr>
              </tbody>
            </table>
          </div>
          <p className="text-base leading-relaxed">
            This flexibility makes it easy for tourists, business travelers, and
            frequent visitors to legally purchase medical cannabis while
            they&rsquo;re in Washington, DC.
          </p>

          <a
            href={REGISTRATION_LINKS.medical}
            target="_blank"
            rel="noreferrer"
            className="mt-8 inline-flex items-center gap-2 bg-brand text-white hover:bg-brand-600 px-6 py-4 text-[11px] uppercase tracking-[0.22em] transition-colors"
            data-testid="blog-register-medical-btn-2"
          >
            Register for Medical Cannabis <ArrowUpRight size={14} />
          </a>

          <div className="my-16 border-t border-black/10" />

          {/* WE'RE HERE TO HELP */}
          <h2 className="font-serif text-3xl sm:text-4xl text-ink mt-2 mb-5">
            We&rsquo;re here to help
          </h2>
          <p className="text-lg leading-relaxed">
            At District Wellness Center, our goal is to make your experience as
            smooth and welcoming as possible. If you have questions about
            registration, self-certification, or qualifying as an out-of-state
            patient, our knowledgeable staff is happy to walk you through the
            process.
          </p>
          <p className="text-lg leading-relaxed mt-4">
            Whether you&rsquo;re a first-time patient or an experienced medical
            cannabis consumer, we&rsquo;re committed to providing premium
            products, trusted education, and exceptional service every step of
            the way.
          </p>
        </div>
      </article>

      {/* FINAL CTA */}
      <section className="bg-ink text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-4xl sm:text-5xl leading-[1.05]">
            Visit District Wellness Center today.
          </h2>
          <p className="mt-5 text-white/70 max-w-2xl mx-auto text-base sm:text-lg leading-relaxed">
            Getting access to medical cannabis in Washington, DC has never been
            easier. Most patients can complete the registration process in just a
            few minutes and begin shopping the very same day. Stop by District
            Wellness Center and let us help you get started.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href={REGISTRATION_LINKS.medical}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 bg-brand hover:bg-brand-600 text-white px-7 py-4 text-[11px] uppercase tracking-[0.22em] transition-colors"
              data-testid="blog-final-register-medical-btn"
            >
              Register for Medical Cannabis <ArrowUpRight size={14} />
            </a>
          </div>

          <div className="mt-14 pt-10 border-t border-white/10 grid sm:grid-cols-3 gap-6 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-1">Location</div>
              <div>4915 Georgia Ave NW, Washington DC</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-1">Hours</div>
              <div>Open 7 days · Mon–Thu 8am–10pm · Fri–Sat 8am–11pm · Sun 9am–9pm</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-1">Related reading</div>
              <Link to="/education" className="hover:text-brand transition-colors">
                Education & FAQs →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
